// src/components/DiagnosisDetailDialog.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axios from '../api/axios';

interface UserProfile {
  full_name?: string;
  furigana?: string;
  gender?: string;
  birth_date?: string;
  phone_number?: string;
  address?: string;

  chart_number?: string | null;
}

interface Reservation {
  id: number;
  service: string;
  date: string;
  time: string;
  status: string;
  payment_status?: string | null;
  visit_type: string;
  zoom_join_url?: string;
  diagnosis?: string;
  prescription_text?: string;
  notes?: string;
  user_profile?: UserProfile;
  doctor_name?: string | null;

  chart_number?: string | null;
  patient_chart_number?: string | null;
}

interface Props {
  reservation: Reservation;
  onClose: () => void;
}

const DiagnosisDetailDialog: React.FC<Props> = ({ reservation, onClose }) => {
  const { t } = useTranslation();

  // 診療録(カルテ)PDFを認証付きエンドポイントから取得して表示・保存
  const handleDownloadKarte = async () => {
    try {
      const res = await axios.get(`/receipts/karte/${reservation.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      alert(t('mypage.karte_download_failed') || '診療録をダウンロードできませんでした');
    }
  };

  const profile = reservation?.user_profile || {};
  const birthday =
    profile?.birth_date && dayjs(profile.birth_date).isValid()
      ? dayjs(profile.birth_date)
      : null;
  const age = birthday ? dayjs().diff(birthday, 'year') : null;

  const clean = (v: unknown) => {
    const s = (v ?? '') as string;
    return String(s).trim() || null;
  };

  const chartNumber =
    clean(reservation?.chart_number) ??
    clean(reservation?.patient_chart_number) ??
    clean(profile?.chart_number) ??
    null;

  const NA = t('na') || '-';

  // 患者情報：PC は 2 組/行（＝従来の 4 列）、スマホは 1 組/行（2 列）で表示する。
  // 並び順は従来の表と同一（氏名・生年月日 / フリガナ・性別 / 電話・住所 / カルテ番号・診療科目）。
  const patientFields: { label: string; value: React.ReactNode }[] = [
    { label: t('doctor.name'), value: profile.full_name || NA },
    {
      label: t('doctor.birthday'),
      value: birthday
        ? `${birthday.format('YYYY-MM-DD')}（${age}${t('doctor.years_old')}）`
        : NA,
    },
    { label: t('doctor.furigana'), value: profile.furigana || NA },
    { label: t('doctor.gender'), value: profile.gender || NA },
    { label: t('doctor.phone'), value: profile.phone_number || NA },
    { label: t('doctor.address'), value: profile.address || NA },
    { label: t('doctor.chart_number') || 'カルテ番号', value: chartNumber || NA },
    { label: t('doctor.service'), value: t(`service.${reservation.service}`) || reservation.service },
  ];

  // 診療内容：ラベル列は内容に合わせて最小幅にし、狭い画面でも本文欄を広く取る
  const recordFields: { label: string; value: string }[] = [
    { label: t('doctor.diagnosis'), value: reservation.diagnosis || '' },
    { label: t('doctor.prescription'), value: reservation.prescription_text || '' },
    { label: t('doctor.notes'), value: reservation.notes || '' },
  ];

  // グリッドで罫線を組む：外枠は上/左、各セルは下/右を持たせて表組みに見せる
  const dtClass = 'border-b border-r border-gray-300 bg-gray-50 px-2 py-1 m-0 whitespace-nowrap text-gray-600';
  // min-w-0：1fr 列の最小幅は min-content になるため、textarea の固有幅や
  // 途中で折り返せない長い文字列があると列がはみ出す。それを許容縮小させる。
  const ddClass = 'min-w-0 border-b border-r border-gray-300 px-2 py-1 m-0 break-words';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl p-4 sm:p-6 max-w-2xl w-full shadow-lg overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-bold mb-4">{t('mypage.diagnosis_detail')}</h2>

        {/* 患者情報（スマホ=1組/行・PC=2組/行） */}
        <dl className="mb-6 grid grid-cols-[minmax(5rem,auto)_1fr] md:grid-cols-[minmax(5rem,auto)_1fr_minmax(5rem,auto)_1fr] border-t border-l border-gray-300 text-sm">
          {patientFields.map((f, i) => (
            <React.Fragment key={i}>
              <dt className={dtClass}>{f.label}</dt>
              <dd className={ddClass}>{f.value}</dd>
            </React.Fragment>
          ))}
        </dl>

        {/* 診療内容（診断・処方・メモ） */}
        <dl className="mb-6 grid grid-cols-[minmax(4rem,auto)_1fr] border-t border-l border-gray-300 text-sm">
          {recordFields.map((f, i) => (
            <React.Fragment key={i}>
              <dt className={dtClass}>{f.label}</dt>
              <dd className={ddClass}>
                <textarea
                  value={f.value}
                  readOnly
                  className="w-full border rounded p-2 bg-gray-100"
                  rows={3}
                />
              </dd>
            </React.Fragment>
          ))}
        </dl>

        {/* 医師署名 */}
        {reservation.doctor_name && (
          <div className="text-right text-xs text-gray-500 mb-4">
            {t('doctor.doctor_sign') || '医師署名'}: {reservation.doctor_name} {t('doctor.doctor_title_suffix') || '医師'}
          </div>
        )}

        <div className="flex justify-end gap-2">
          {/* カルテは支払い確定時に生成されるため、支払い済みのみダウンロード可 */}
          {reservation.payment_status === 'paid' && (
            <button
              onClick={handleDownloadKarte}
              className="px-4 py-2 border border-coral text-coral rounded-full transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral/5"
            >
              {t('mypage.download_karte') || '診療録をダウンロード'}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral"
          >
            {t('mypage.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosisDetailDialog;
